import { Link } from "react-router-dom";

export function Hero({ eyebrow, title, subtitle, children, image, imageAlt, badge }) {
  return (
    <section className="ssc-hero">
      {/* Animated blobs */}
      <div className="ssc-hero-blob" style={{ width: 420, height: 420, background: "radial-gradient(circle, rgba(0,230,255,0.55), transparent 60%)", top: "-160px", right: "-120px" }} />
      <div className="ssc-hero-blob" style={{ width: 360, height: 360, background: "radial-gradient(circle, rgba(255,45,74,0.45), transparent 60%)", bottom: "-160px", left: "-120px", animationDelay: "2s" }} />

      <div className="ssc-container py-14 md:py-24 grid md:grid-cols-2 gap-10 md:gap-14 items-center relative">
        <div className="ssc-fade-in">
          {eyebrow && <div className="ssc-eyebrow mb-5">{eyebrow}</div>}
          {badge}
          <h1 className="ssc-display text-4xl md:text-[64px] xl:text-[80px] font-black leading-[0.95]">
            {title}
          </h1>
          {subtitle && <p className="mt-5 text-lg md:text-xl text-[var(--ssc-silver)] max-w-xl leading-relaxed">{subtitle}</p>}
          {children && <div className="mt-7">{children}</div>}
        </div>
        {image && (
          <div className="ssc-fade-in relative">
            {/* Decorative outline behind the photo */}
            <div className="absolute inset-0 -translate-x-3 translate-y-3 rounded-[28px] border border-[var(--ssc-cyan)]/40" aria-hidden="true" />
            <div className="absolute inset-0 translate-x-3 -translate-y-3 rounded-[28px] border border-[var(--ssc-red)]/40" aria-hidden="true" />
            <img
              src={image}
              alt={imageAlt || ""}
              className="relative w-full h-auto rounded-[28px] shadow-2xl border border-white/10 object-cover aspect-[4/5] md:aspect-[5/6]"
              loading="eager"
            />
            {/* Floating "live inventory" chip on the photo */}
            <div className="hidden md:flex items-center gap-2 absolute -left-4 bottom-8 bg-[var(--ssc-cyan)] text-[#02101a] font-extrabold text-sm px-4 py-2 rounded-full shadow-[0_10px_30px_rgba(0,230,255,0.45)]">
              <span className="w-2 h-2 rounded-full bg-[#02101a] animate-pulse" />
              Live inventory
            </div>
            <div className="hidden md:block absolute -right-4 top-8 bg-white text-[var(--ssc-navy)] font-extrabold text-sm px-4 py-2 rounded-full shadow-2xl border border-white/40">
              No-pressure shopping
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, center }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-10 md:mb-14`}>
      {eyebrow && <div className="ssc-eyebrow mb-4">{eyebrow}</div>}
      <h2 className="ssc-display text-3xl md:text-5xl font-black text-[var(--ssc-navy)] leading-[1.05]">{title}</h2>
      {subtitle && <p className="mt-4 text-[var(--ssc-ink-2)] text-base md:text-lg">{subtitle}</p>}
    </div>
  );
}

export function CtaCard({ to, ext, title, description, icon, testId }) {
  const inner = (
    <div className="ssc-card h-full flex flex-col group" data-testid={testId}>
      {icon && (
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[var(--ssc-cyan)] to-[var(--ssc-blue)] grid place-items-center text-[#02101a] mb-3 shadow-md">
          {icon}
        </div>
      )}
      <div className="text-lg font-extrabold text-[var(--ssc-navy)]">{title}</div>
      {description && <p className="text-sm text-[var(--ssc-ink-2)] mt-1">{description}</p>}
      <div className="mt-4 inline-flex items-center gap-1 text-[var(--ssc-blue)] font-extrabold group-hover:translate-x-1 transition-transform">
        Learn more <span aria-hidden>→</span>
      </div>
    </div>
  );
  if (ext) return <a href={to} target="_blank" rel="noopener noreferrer">{inner}</a>;
  return <Link to={to}>{inner}</Link>;
}

export function Disclaimer({ text }) {
  return <p className="text-xs text-[var(--ssc-ink-2)] leading-relaxed">{text}</p>;
}

export function PageContainer({ children, className = "" }) {
  return <main id="main-content" className={`ssc-container ssc-section ${className}`}>{children}</main>;
}
